import { Link } from "react-router";

import { FingerprintIcon, TrophyIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  DetailField,
  DetailGrid,
  DetailSection,
  RawJsonCard,
  ShowHero,
  SystemSection,
  fmtDate,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LeagueStandingsSection } from "@/pages/leagues/components/league-standings-section";
import {
  LeagueViewType,
  leagueViewSchema,
} from "@/pages/leagues/schemas/league.view";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";

export const LeagueShow = () => {
  const { record, raw } = useShowRecord<LeagueViewType>(
    leagueViewSchema,
    (r) => `League | ${r.name ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              poster={record.media?.photos?.[0]}
              eyebrow={record.sport}
              title={record.name ?? "Untitled"}
              subtitle={record.country}
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                </>
              }
            />

            <div className="flex justify-end">
              <Button asChild variant="outline">
                <Link to={`/matches?leagueId=${record.id}`}>Matches</Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Details"
                icon={<TrophyIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Sport" value={record.sport} />
                  <DetailField label="Country" value={record.country} />
                  <DetailField label="Gender" value={record.gender} />
                  <DetailField label="Age group" value={record.ageGroup} />
                  <DetailField label="Tier" value={record.tier} />
                  <DetailField
                    label="Enabled"
                    value={
                      record.enabled == null
                        ? undefined
                        : record.enabled
                          ? "Yes"
                          : "No"
                    }
                  />
                  <DetailField label="Status" value={record.status} />
                  <DetailField
                    label="Hidden"
                    value={
                      record.hide == null
                        ? undefined
                        : record.hide
                          ? "Yes"
                          : "No"
                    }
                  />
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Source"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied league ID" />
                    </span>
                  </DetailField>
                  <DetailField
                    label="Highlightly ID"
                    value={record.metadata?.sourceId}
                    mono
                  />
                  <DetailField
                    label="Current season"
                    value={record.currentSeason}
                  />
                  <DetailField
                    label="Season starts"
                    value={
                      record.seasonStart
                        ? fmtDate(record.seasonStart)
                        : undefined
                    }
                  />
                  <DetailField
                    label="Season ends"
                    value={
                      record.seasonEnd ? fmtDate(record.seasonEnd) : undefined
                    }
                  />
                  <DetailField label="Teams" value={record.teamsCount} />
                </DetailGrid>
                <ReadOnlyNote />
              </DetailSection>
            </div>

            <LeagueStandingsSection leagueId={record.id} />

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
